import _ from 'underscore';
import URI from 'urijs';
import { convertServerSideErrors, getURLWithScope, pollTaskItemExecution } from '@bingads-webui-campaign/inline-download-repository-core';

// Avoid fast cleanup before iframe src is fired in chrome
const DELAY_AFTER_DOWNLOAD_TASK_IN_MS = 4000;

export class InlineDownloadReportDataRepository {
  constructor({
    deps,
    delayAfterDownload = DELAY_AFTER_DOWNLOAD_TASK_IN_MS,
    isCustomerLevel = true,
    currentActivity = null,
  }) {
    this.deps = deps;

    this.alertId = null;
    this.delayAfterDownload = delayAfterDownload;
    this.isCustomerLevel = isCustomerLevel;

    if (currentActivity) {
      this.currentActivity = currentActivity;
    }
  }

  generateUrl() {
    return getURLWithScope(
      this.deps.pageContext,
      'TaskItems/SubmitReportDataTaskItem?$expand=TaskItemExecutions',
      this.isCustomerLevel
    );
  }

  download(options) {
    const {
      alertCenter,
      asyncScheduler,
      downloadService,
      getODataErrors,
      odata,
      i18n,
      pageContext,
    } = this.deps;

    const url = this.generateUrl(options);
    const headers = this.generateHeaders(options);
    const data = this.generateBody(options);

    const postDownloadRequest = odata.post(url, {
      headers,
      data,
    }).catch(({ errors } = {}) => {
      throw convertServerSideErrors({ errors, getODataErrors, i18n });
    });

    // post the download request and start polling for it
    return postDownloadRequest.then((response) => {
      if (response && !_.isEmpty(response.TaskItemExecutions)) {
        // add log when get taskItemExecutionId from mt
        if (this.currentActivity) {
          this.currentActivity.trace({
            taskItemExecutionId: response.TaskItemExecutions[0].Id,
          }, 'InlineDownloadReportDataRepository/postDownloadRequest');
        }
        // return the first taskItemExecution so that it can be polled
        return response.TaskItemExecutions[0];
      }
      throw new Error(i18n.getString('GridDownloadFailureMessage'));
    }).then(({ Id }) => {
      // show an alert saying that download request was successfully submitted
      this.alertId = alertCenter.showConfirmation(
        i18n.getString('GridDownloadStatusMessage'),
        this.alertId
      );

      return pollTaskItemExecution({
        taskItemExecutionId: Id,
        i18n,
        pageContext,
        odata,
        asyncScheduler,
        currentActivity: this.currentActivity,
      }).finally(() => alertCenter.dismiss(this.alertId))
        .then(resultUrl => downloadService.download(resultUrl, URI(resultUrl).filename(true)))
        .delay(this.delayAfterDownload);
    });
  }

  /**
   * Generates the headers to be sent to MT.
   * @param {Object} options.Entity - Entity to use for getting the cache hint.
   * @returns {Object} Object representing list of header keys and values.
   */
  generateHeaders({ Entity }) {
    return {
      'x-ms-lastwritetime': this.deps.cacheManager.getCacheHint({
        adapter: 'odata',
        resource: Entity, // this may not be always correct
      }) || 'Max',
    };
  }

  /**
   * Generates the POST body.
   * @param {Object} options - Defines the values set in the body.
   * @returns {Object} Body to be sent in the POST request.
   */
  generateBody(options) {
    const ReportData = _.defaults(options, {
      '@odata.type': '#Model.ReportDataTaskItem',
      SchemaName: 'V1',
      Cron: '',
      ReportAggregation: 'Summary',
    });

    return { ReportData };
  }
}
